/**
 * The six quick-action boards: the header the home places before the agent
 * starts, and the prompt it sends, from one set of words so the two never
 * disagree. Pure data and a string template, so the shots harness can render
 * the headers on their own.
 */

export type HomeAccent = "rust" | "gold" | "plum" | "teal" | "sage";

/** A gentle ambient motif; every one holds still under reduced motion. */
export type HomeMotif = "leaves" | "orbit" | "shimmer";

export interface HomeHeader {
  eyebrow: string;
  title: string;
  /** One word of the title, set in italics and the accent colour. */
  accent: string;
  /** One line, in plain words, on what is coming. */
  lede: string;
  /** A large faint word or glyph on the right. */
  watermark: string;
  color: HomeAccent;
  motif?: HomeMotif;
}

/** One piece of the board, the full ask for the prompt. */
export interface HomePiece {
  text: string;
}

export interface HomeBoard {
  id: string;
  header: HomeHeader;
  /** The header's file name on the canvas. */
  file: string;
  /** The prompt's first line: what the board is. */
  ask: string;
  pieces: readonly HomePiece[];
  /** The prompt's last line: how to lay the pieces out. */
  layout: string;
}

/** The header's box: the board's full width, on the 16px lattice. */
export const HOME_HEADER_WIDTH = 1216;
export const HOME_HEADER_HEIGHT = 176;

const STILL =
  "holds still for people who have asked their system for less motion";

export const HOME_BOARDS: readonly HomeBoard[] = [
  {
    id: "brainstorm",
    file: "Idea Board Header.html",
    header: {
      eyebrow: "Your first canvas · Six ideas",
      title: "What will you build first?",
      accent: "first",
      lede: "Six idea cards, each a different way this canvas can work for you, and one question at the end.",
      watermark: "6",
      color: "gold",
      motif: "orbit",
    },
    ask: "Brainstorm six things I could build on this canvas, as an idea board.",
    pieces: [
      {
        text: "Six idea cards, one HTML block each in a different colour: a title and two lines on what it is and why it is useful. Make them concrete projects a first-time user could start today with what Cohesive can do here: a document, a styled page or interactive block, a set of generated images, a flow diagram, a research summary from the web, a plan.",
      },
      {
        text: "One note asking which one I want to start.",
      },
    ],
    layout:
      "Lay the cards out three by two under the header, the note beneath. Make this interactive, push the limits of HTML5, and make it an impressive first user experience.",
  },
  {
    id: "explain",
    file: "Seasons Board Header.html",
    header: {
      eyebrow: "An explorable · Why we have seasons",
      title: "A year of tilted sunlight",
      accent: "tilted",
      lede: "An orbit you can drag, a cause and effect diagram and a short explainer: the tilt, not the distance, makes the seasons.",
      watermark: "☀",
      color: "rust",
      motif: "leaves",
    },
    ask: "Explain why we have seasons as something I can play with.",
    pieces: [
      {
        text: `One interactive block with two panels: a slider for the day of the year that moves the Earth around the Sun, shows its tilt and shades how sunlight falls on each hemisphere, naming the season for each; and a chart of daylight hours across the year for four latitudes with a marker that follows the same slider. When it opens, let the Earth orbit on its own, a few days per frame, until the slider is touched; it ${STILL}.`,
      },
      {
        text: "A small diagram of shapes and connectors from tilt to sunlight angle to temperature, with at least 160px between connected shapes side by side and 128px stacked.",
      },
      {
        text: "A short document that explains it in plain words and corrects the myth that seasons come from distance to the Sun.",
      },
    ],
    layout:
      "Put the interactive block large on the left under the header; once it is placed, put the diagram and the document in a column to its right, past the right edge the placement reported, so nothing overlaps. Aim to finish within five minutes.",
  },
  {
    id: "game",
    file: "Word Game Header.html",
    header: {
      eyebrow: "A little game · Five letters, six tries",
      title: "Guess the word",
      accent: "word",
      lede: "The puzzle and its rules: type or tap a five-letter guess and the tiles tell you how close you are.",
      watermark: "W",
      color: "teal",
      motif: "shimmer",
    },
    ask: "Build a small word game I can play right away.",
    pieces: [
      {
        text: "One game block with two panels: the puzzle, guess a hidden five-letter word in six tries, with tiles that flip to show which letters are right, misplaced or absent, an on-screen keyboard so it works by click as well as typing, a win and a lose state, and a New game button that picks another word from a built-in list of at least fifty; and a stats panel with this session's wins, streak and guess distribution as a small bar chart that updates as I play.",
      },
      {
        text: "A rules block that shows the rules as three example rows.",
      },
    ],
    layout:
      "Put the game block large in the middle under the header and the rules beside it. Aim to finish within five minutes.",
  },
  {
    id: "brand",
    file: "Brand Board Header.html",
    header: {
      eyebrow: "Brand board · A Lisbon coffee roaster",
      title: "A Lisbon roaster, from bean to brand",
      accent: "bean",
      lede: "A logo, a palette, a type specimen, a landing page and a short brand guide for a small independent roaster.",
      watermark: "Lisboa",
      color: "rust",
      motif: "shimmer",
    },
    ask: "Invent a small independent coffee roaster in Lisbon and build its brand board.",
    pieces: [
      {
        text: "One generated logo: an image of a logo concept on a plain background.",
      },
      {
        text: "A palette block with five swatches, their hex codes and where each is used.",
      },
      {
        text: "A type specimen block pairing a display face and a text face at several sizes.",
      },
      {
        text: `A landing page mockup block for the roaster with a hero, three product cards and a newsletter signup; the headline settles in gently on load and ${STILL}.`,
      },
      {
        text: "A short brand guide document: the name, the story in three sentences, tone of voice, and do and don't examples.",
      },
    ],
    layout:
      "Arrange it like a designer's board under the header: the logo top left, palette and type under it, the landing page large on the right, the guide below. Keep the board in English, with Portuguese only in the roaster's own names and slogans. Aim to finish within five minutes.",
  },
  {
    id: "headlines",
    file: "Front Page Header.html",
    header: {
      eyebrow: "The Cohesive Times · Front page",
      title: "Today's headlines, set in print",
      accent: "headlines",
      lede: "Today's stories in technology, science, business and culture, then the world, with a pull quote and the full summaries.",
      watermark: "¶",
      color: "plum",
    },
    ask: "Summarize today's headlines as the front page of a newspaper called The Cohesive Times.",
    pieces: [
      {
        text: "A web search first, for today's top stories from reputable sources, leading with technology, science, business and culture, world news after, and no partisan politics.",
      },
      {
        text: "A lead story block with a headline, a one-line deck and a short summary.",
      },
      {
        text: "Four to six story cards, one block each, with a headline, a one-line summary and the source as a small footer line.",
      },
      {
        text: "One pull-quote block.",
      },
      {
        text: "A document titled with today's date holding the full summaries and source links for every story.",
      },
    ],
    layout:
      "Lay it out as a page under the header, which is the paper's masthead: the lead story across the top, the story cards in two columns below, the document to the right. Aim to finish within five minutes.",
  },
  {
    id: "art",
    file: "Living Art Header.html",
    header: {
      eyebrow: "Living art · A flow field",
      title: "Art that draws itself",
      accent: "draws",
      lede: "A flow field of thousands of particles that bends around your pointer, and a short note on how it works.",
      watermark: "∞",
      color: "teal",
      motif: "shimmer",
    },
    ask: "Make a piece of living art that draws itself while I watch.",
    pieces: [
      {
        text: `One art block: a flow field of thousands of particles drawn on a canvas element, moving from the moment it opens, drifting through a palette that shifts slowly and bending around the pointer. A slim control bar with palette (four choices), density, speed, a Reroll seed button and a Freeze switch, and a panel that shows the current seed and palette as a small poster card with a title it invents. It draws one settled frame instead and ${STILL}.`,
      },
      {
        text: "A document that explains how a flow field works in five short paragraphs.",
      },
    ],
    layout:
      "Put the art block large on the left under the header and the document on the right. Aim to finish within five minutes.",
  },
];

export function homeBoard(id: string): HomeBoard | undefined {
  return HOME_BOARDS.find((board) => board.id === id);
}

export function quickActionPrompt(board: HomeBoard): string {
  return [
    board.ask,
    ...board.pieces.map((piece) => `- ${piece.text}`),
    board.layout,
  ].join("\n");
}

const escape = (text: string) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** The title with its accent word in italics. */
function titleMarkup({ title, accent }: HomeHeader): string {
  const at = title.indexOf(accent);
  if (at < 0) return escape(title);
  return `${escape(title.slice(0, at))}<em>${escape(accent)}</em>${escape(title.slice(at + accent.length))}`;
}

const MOTIFS: Record<HomeMotif, { css: string; html: string }> = {
  leaves: {
    css: `.leaf{position:absolute;top:-30px;width:18px;height:18px;opacity:0;animation:fall linear infinite}
.leaf svg{width:100%;height:100%;animation:sway ease-in-out infinite alternate}
@keyframes fall{0%{transform:translate(0,-10px) rotate(0);opacity:0}10%{opacity:.7}85%{opacity:.5}100%{transform:translate(var(--dx),260px) rotate(var(--rot));opacity:0}}
@keyframes sway{from{transform:rotate(-16deg)}to{transform:rotate(16deg)}}
@media (prefers-reduced-motion:reduce){.leaf{opacity:.3;top:var(--sy)}}`,
    html: `<div class="ambient" id="leaves"></div>
<script>
(function(){
 var colors=['var(--rust)','var(--gold)','var(--plum)','var(--sage)'];
 var path='M12 1l2 5 4-2-1 5 5 1-4 3 2 4-5-1-1 5-2-3-2 3-1-5-5 1 2-4-4-3 5-1-1-5 4 2z';
 var box=document.getElementById('leaves');
 if(!box||box.childElementCount)return;
 for(var i=0;i<12;i++){
  var d=document.createElement('div');d.className='leaf';
  var s=12+Math.random()*12;d.style.width=d.style.height=s+'px';
  d.style.left=(30+Math.random()*68)+'%';
  d.style.setProperty('--dx',(Math.random()*120-30)+'px');
  d.style.setProperty('--rot',(Math.random()*480-240)+'deg');
  d.style.setProperty('--sy',(10+Math.random()*70)+'%');
  d.style.animationDuration=(10+Math.random()*8)+'s';
  d.style.animationDelay=(Math.random()*10)+'s';
  d.innerHTML='<svg viewBox="0 0 24 24" style="animation-duration:'+(2+Math.random()*2)+'s"><path d="'+path+'" fill="'+colors[i%colors.length]+'"/></svg>';
  box.appendChild(d);
 }
})();
</script>`,
  },
  orbit: {
    css: `.orbit{inset:auto;right:150px;top:50%;width:140px;height:140px;margin-top:-70px;opacity:.55}
.orbit svg{width:100%;height:100%;overflow:visible}
.ring{animation:spin 48s linear infinite;transform-origin:80px 80px}
@keyframes spin{to{transform:rotate(360deg)}}`,
    html: `<div class="ambient orbit"><svg viewBox="0 0 160 160">
<circle cx="80" cy="80" r="62" fill="none" stroke="var(--n-20)" stroke-dasharray="2 6"/>
<g class="ring">
<circle cx="80" cy="18" r="5" fill="var(--gold)"/><circle cx="133.7" cy="49" r="5" fill="var(--teal)"/>
<circle cx="133.7" cy="111" r="5" fill="var(--plum)"/><circle cx="80" cy="142" r="5" fill="var(--sage)"/>
<circle cx="26.3" cy="111" r="5" fill="var(--rust)"/><circle cx="26.3" cy="49" r="5" fill="var(--sticky-sea)"/>
</g></svg></div>`,
  },
  shimmer: {
    css: `.glow{position:absolute;inset:-40% -10%;background:radial-gradient(closest-side,color-mix(in srgb,var(--accent) 22%,transparent),transparent 70%);animation:drift 16s ease-in-out infinite alternate}
@keyframes drift{from{transform:translate(-8%,-6%) scale(1)}to{transform:translate(14%,8%) scale(1.15)}}`,
    html: `<div class="ambient glow"></div>`,
  },
};

/** The header block's document, styled with the block tokens like any agent block. */
export function renderHomeHeader(board: HomeBoard): string {
  const { header } = board;
  const motif = header.motif ? MOTIFS[header.motif] : null;
  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>${escape(header.title)}</title>
<style>
html,body{margin:0;height:100%;background:transparent;overflow:hidden}
*{box-sizing:border-box}
body{--accent:var(--${header.color});font-family:var(--font-sans);color:var(--n-90)}
.board{position:relative;height:100%;border-radius:20px;border:1px solid var(--n-10);overflow:hidden;display:flex;align-items:center;padding:24px 44px;background:linear-gradient(120deg,color-mix(in srgb,var(--accent) 16%,var(--n-00)),color-mix(in srgb,var(--accent) 6%,var(--n-00)) 55%,var(--n-00))}
.ambient{position:absolute;inset:0;pointer-events:none;z-index:0}
.text{position:relative;z-index:1;display:flex;flex-direction:column;gap:9px;max-width:66%}
.eyebrow{font:600 11px/1 var(--font-mono);letter-spacing:.18em;text-transform:uppercase;color:color-mix(in srgb,var(--accent) 65%,var(--n-90));opacity:0;animation:rise .8s ease-out .05s forwards}
h1{margin:0;font-family:var(--font-serif);font-weight:500;font-size:46px;line-height:1.04;letter-spacing:-.015em;color:var(--n-95);opacity:0;animation:rise 1s cubic-bezier(.2,.7,.2,1) .2s forwards}
h1 em{font-style:italic;color:color-mix(in srgb,var(--accent) 75%,var(--n-95))}
.lede{margin:0;font-family:var(--font-serif);font-style:italic;font-size:17px;line-height:1.4;color:var(--text-muted);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;opacity:0;animation:fade 1.2s ease-out .7s forwards}
.mark{position:absolute;right:36px;top:50%;transform:translateY(-54%);font-family:var(--font-serif);font-size:${header.watermark.length > 1 ? "96px" : "150px"};font-style:${header.watermark.length > 1 ? "italic" : "normal"};line-height:1;color:color-mix(in srgb,var(--accent) 60%,var(--n-95));opacity:.08;pointer-events:none;user-select:none;z-index:0}
@keyframes rise{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
@keyframes fade{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}
${motif?.css ?? ""}
@media (prefers-reduced-motion:reduce){*{animation:none!important}.eyebrow,h1,.lede{opacity:1;transform:none}}
</style></head>
<body>
<div class="board">
${motif?.html ?? ""}
<div class="mark" aria-hidden="true">${escape(header.watermark)}</div>
<div class="text">
 <div class="eyebrow" data-edit-id="eyebrow">${escape(header.eyebrow)}</div>
 <h1 data-edit-id="title">${titleMarkup(header)}</h1>
 <p class="lede" data-edit-id="lede">${escape(header.lede)}</p>
</div>
</div>
</body></html>
`;
}
